import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, UserPlus, Users, Pencil, Trash2, KeyRound, ShieldCheck, Search, X, Check } from "lucide-react";

const PERMISSION_KEYS = [
  "jobs","adas","estimate_analyzer","calibration_reports","invoices",
  "sales","commissions","master_data","equipment","work_orders",
  "reports","users","settings","financials",
];
const PERM_LABELS = { jobs:"Jobs", adas:"ADAS", estimate_analyzer:"Estimate Analyzer", calibration_reports:"Cal Reports", invoices:"Invoices", sales:"Sales/CRM", commissions:"Commissions", master_data:"Master Data", equipment:"Equipment", work_orders:"Work Orders", reports:"Reports", users:"Users Mgmt", settings:"Settings", financials:"Financials" };
const ROLES = [
  {value:"admin", label:"Administrator"},
  {value:"manager", label:"Manager"},
  {value:"technician", label:"ADAS Technician"},
  {value:"sales", label:"Sales Rep"},
  {value:"finance", label:"Finance"},
  {value:"reception", label:"Reception"},
];
const ROLE_DEFAULTS = {
  admin: PERMISSION_KEYS.reduce((a,k)=>({...a,[k]:true}),{}),
  manager: PERMISSION_KEYS.reduce((a,k)=>({...a,[k]:!["users","financials"].includes(k)}),{}),
  technician: PERMISSION_KEYS.reduce((a,k)=>({...a,[k]:["jobs","adas","calibration_reports","work_orders","equipment"].includes(k)}),{}),
  sales: PERMISSION_KEYS.reduce((a,k)=>({...a,[k]:["jobs","sales","commissions","estimate_analyzer","reports"].includes(k)}),{}),
  finance: PERMISSION_KEYS.reduce((a,k)=>({...a,[k]:["invoices","commissions","reports","financials","jobs"].includes(k)}),{}),
  reception: PERMISSION_KEYS.reduce((a,k)=>({...a,[k]:["jobs","sales","master_data"].includes(k)}),{}),
};
const STATUS_STYLE = {
  active: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  pending_approval: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  disabled: "bg-slate-500/15 border-slate-500/40 text-slate-400",
  rejected: "bg-rose-500/15 border-rose-500/40 text-rose-300",
};

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/users"); setRows(r.data || []); }
    catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    if (!t) return rows;
    return rows.filter((u) =>
      (u.name || "").toLowerCase().includes(t) ||
      (u.email || "").toLowerCase().includes(t) ||
      (u.role || "").toLowerCase().includes(t)
    );
  }, [rows, q]);

  const del = async (u) => {
    if (!window.confirm(`Deletar ${u.name}? Isso não pode ser desfeito.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Deletado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-blue-400 mb-2 flex items-center gap-1.5"><Users className="w-3 h-3" /> ADMIN</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Gestão de usuários</h1>
          <p className="text-sm text-slate-400 mt-2">Criar, editar, resetar senha, ativar/desativar ou deletar contas da sua empresa.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="create-user-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Novo usuário
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, email ou role..." className="bg-slate-950 border-slate-800 pl-9" data-testid="user-search" />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} de {rows.length}</span>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-blue-400 mx-auto" /></div>
      ) : (
        <Card className="bg-[#0B1120] border-slate-800 p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/40">
              <tr>
                <th className="text-left p-3">Nome / Email</th>
                <th className="text-left">Role</th>
                <th className="text-left">Status</th>
                <th className="text-left">Cargo</th>
                <th className="text-right pr-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-slate-800/50 hover:bg-slate-900/30" data-testid={`user-row-${u.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-[11px] text-slate-500">{u.email}</div>
                  </td>
                  <td><Badge className="bg-blue-500/15 border-blue-500/40 text-blue-300 uppercase text-[10px]">{u.role}</Badge></td>
                  <td><Badge className={`${STATUS_STYLE[u.status] || STATUS_STYLE.active} uppercase text-[10px]`}>{u.status || "active"}</Badge></td>
                  <td className="text-xs text-slate-400">{u.job_title || "—"}</td>
                  <td className="text-right pr-3">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setEditing(u)} className="border-slate-700 h-7 px-2" data-testid={`edit-${u.id}`}><Pencil className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => setResetting(u)} className="border-amber-500/40 text-amber-300 h-7 px-2" data-testid={`reset-${u.id}`}><KeyRound className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => del(u)} className="border-rose-500/40 text-rose-300 h-7 px-2" data-testid={`del-${u.id}`}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">Nenhum usuário</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {creating && <UserFormDialog user={null} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {editing && <UserFormDialog user={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}

function UserFormDialog({ user, onClose, onDone }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", password: "",
    role: user?.role || "technician", phone: user?.phone || "", job_title: user?.job_title || "",
    department: user?.department || "", status: user?.status || "active",
    can_view_all_leads: !!user?.can_view_all_leads,
    commission_type: user?.commission_type || "", commission_rate: user?.commission_rate || "",
  });
  const [perms, setPerms] = useState(() =>
    user?.permissions && Object.keys(user.permissions).length > 0
      ? { ...ROLE_DEFAULTS[form.role], ...user.permissions }
      : ROLE_DEFAULTS[form.role] || {}
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) setPerms(ROLE_DEFAULTS[form.role] || {});
  }, [form.role, isEdit]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, role: form.role, phone: form.phone,
        job_title: form.job_title, department: form.department,
        permissions: perms, can_view_all_leads: form.can_view_all_leads,
        commission_type: form.commission_type || null,
        commission_rate: form.commission_rate ? parseFloat(form.commission_rate) : null,
      };
      if (isEdit) {
        payload.status = form.status;
        await api.patch(`/admin/users/${user.id}`, payload);
        toast.success("Atualizado");
      } else {
        payload.email = form.email; payload.password = form.password;
        await api.post("/admin/users", payload);
        toast.success("Usuário criado");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1120] border-slate-800 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="user-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? `Editar ${user.name}` : "Novo usuário"}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Nome completo</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" data-testid="uf-name" /></div>
            <div><Label className="text-xs">Telefone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
          </div>
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" data-testid="uf-email" /></div>
              <div><Label className="text-xs">Senha inicial</Label><Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} minLength={6} className="bg-slate-950 border-slate-800 mt-1" data-testid="uf-password" /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Cargo</Label><Input value={form.job_title} onChange={(e) => setForm({...form, job_title: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="ADAS Tech II" /></div>
            <div><Label className="text-xs">Departamento</Label><Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Função</Label>
              <select value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm mt-1" data-testid="uf-role">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {isEdit && (
              <div>
                <Label className="text-xs">Status</Label>
                <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm mt-1" data-testid="uf-status">
                  <option value="active">active</option>
                  <option value="disabled">disabled</option>
                  <option value="pending_approval">pending_approval</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>
            )}
          </div>
          {form.role === "sales" && (
            <div className="grid grid-cols-3 gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="col-span-3 text-[10px] uppercase tracking-wider font-bold text-emerald-300">Configurações de vendedor</div>
              <div>
                <Label className="text-xs">Tipo comissão</Label>
                <select value={form.commission_type} onChange={(e) => setForm({...form, commission_type: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm mt-1">
                  <option value="">—</option>
                  <option value="percentage">% do total</option>
                  <option value="fixed">Valor fixo</option>
                </select>
              </div>
              <div><Label className="text-xs">Taxa</Label><Input value={form.commission_rate} onChange={(e) => setForm({...form, commission_rate: e.target.value})} className="bg-slate-950 border-slate-800 mt-1" placeholder="10 ou 50" /></div>
              <label className="flex items-center gap-2 mt-6 text-xs cursor-pointer">
                <input type="checkbox" checked={form.can_view_all_leads} onChange={(e) => setForm({...form, can_view_all_leads: e.target.checked})} className="accent-emerald-500" data-testid="uf-can-view-all" />
                Vê leads de todos
              </label>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-2">Permissões (matriz)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {PERMISSION_KEYS.map((k) => (
                <label key={k} className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs cursor-pointer ${perms[k] ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-slate-800 bg-slate-950 text-slate-400"}`} data-testid={`uf-perm-${k}`}>
                  <input type="checkbox" checked={!!perms[k]} onChange={() => setPerms({...perms, [k]: !perms[k]})} className="accent-emerald-500" />
                  {PERM_LABELS[k]}
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
            <Button variant="outline" onClick={onClose} className="border-slate-700"><X className="w-3.5 h-3.5 mr-1.5" />Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="uf-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
              {isEdit ? "Salvar" : "Criar usuário"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (pw.length < 6) return toast.error("Min 6 caracteres");
    setSaving(true);
    try { await api.post(`/admin/users/${user.id}/reset-password`, { new_password: pw }); toast.success(`Senha resetada para ${user.name}`); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1120] border-amber-500/40 max-w-md" data-testid="reset-pw-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-400" /> Reset password: {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
            Isso vai substituir a senha atual. Anote e envie pro usuário por canal seguro.
          </div>
          <div><Label>Nova senha</Label><Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} className="bg-slate-950 border-slate-800 mt-1 font-mono" placeholder="min 6 caracteres" data-testid="reset-pw-input" /></div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-800">
            <Button variant="outline" onClick={onClose} className="border-slate-700">Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-500" data-testid="reset-pw-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1.5" />}
              Salvar nova senha
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
